import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  MessageSquareReply,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  Clock,
  Hash,
  MessageCircle,
  Moon,
  Zap,
  GripVertical,
  Instagram,
  Facebook,
} from 'lucide-react';
import { SocialAutoReplyRule, AutoReplyTriggerType, SocialPlatform, PLATFORM_INFO } from '@/types/social';
import { cn } from '@/lib/core/utils';

interface AutoReplyRulesListProps {
  rules: SocialAutoReplyRule[];
  isLoading: boolean;
  onCreateRule: () => void;
  onEditRule: (rule: SocialAutoReplyRule) => void;
  onDeleteRule: (ruleId: string) => Promise<void>;
  onToggleRule: (ruleId: string, isActive: boolean) => Promise<void>;
  onDuplicateRule?: (rule: SocialAutoReplyRule) => void;
}

// Trigger type icons and labels
const TRIGGER_TYPE_INFO: Record<AutoReplyTriggerType, { icon: React.ElementType; label: string; color: string }> = {
  keyword: { icon: Hash, label: 'Keyword Match', color: 'text-blue-600' },
  first_message: { icon: MessageCircle, label: 'First Message', color: 'text-green-600' },
  out_of_hours: { icon: Moon, label: 'Out of Hours', color: 'text-purple-600' },
  all: { icon: Zap, label: 'All Messages', color: 'text-orange-600' },
};

// Platform icons
const PLATFORM_ICONS: Record<SocialPlatform, React.ElementType> = {
  instagram: Instagram,
  facebook: Facebook,
  linkedin: () => <span className="font-bold text-xs">in</span>,
  tiktok: () => <span className="font-bold text-xs">TT</span>,
};

export const AutoReplyRulesList: React.FC<AutoReplyRulesListProps> = ({
  rules,
  isLoading,
  onCreateRule,
  onEditRule,
  onDeleteRule,
  onToggleRule,
  onDuplicateRule,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<SocialAutoReplyRule | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (rule: SocialAutoReplyRule) => {
    setRuleToDelete(rule);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!ruleToDelete) return;

    setIsDeleting(true);
    try {
      await onDeleteRule(ruleToDelete.id);
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggle = async (rule: SocialAutoReplyRule) => {
    await onToggleRule(rule.id, !rule.is_active);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Auto-Reply Rules</h2>
            <p className="text-muted-foreground">Loading rules...</p>
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="py-4">
                <div className="h-16 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Auto-Reply Rules</h2>
          <p className="text-muted-foreground">
            {rules.length > 0
              ? `${rules.filter(r => r.is_active).length} active rules`
              : 'Set up automatic responses to messages'}
          </p>
        </div>
        <Button onClick={onCreateRule}>
          <Plus className="h-4 w-4 mr-2" />
          Create Rule
        </Button>
      </div>

      {/* Empty state */}
      {rules.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <MessageSquareReply className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Auto-Reply Rules</h3>
            <p className="text-muted-foreground max-w-md mx-auto mb-6">
              Create rules to automatically respond to messages based on keywords,
              first-time contacts, or out-of-hours periods.
            </p>
            <Button onClick={onCreateRule}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Rule
            </Button>
          </CardContent>
        </Card>
      ) : (
        /* Rules list */
        <div className="space-y-3">
          {rules.map((rule, index) => {
            const triggerInfo = TRIGGER_TYPE_INFO[rule.trigger_type];
            const TriggerIcon = triggerInfo.icon;

            return (
              <Card
                key={rule.id}
                className={cn(
                  'transition-all',
                  !rule.is_active && 'opacity-60'
                )}
              >
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    {/* Drag handle (visual only for now) */}
                    <div className="flex-shrink-0 pt-1 text-muted-foreground cursor-grab">
                      <GripVertical className="h-5 w-5" />
                    </div>

                    {/* Priority badge */}
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </div>
                    </div>

                    {/* Rule content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold truncate">{rule.name}</h4>
                        {!rule.is_active && (
                          <Badge className="text-xs">Paused</Badge>
                        )}
                      </div>

                      {/* Trigger info */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <TriggerIcon className={cn('h-4 w-4', triggerInfo.color)} />
                        <span>{triggerInfo.label}</span>
                        {rule.trigger_type === 'keyword' && rule.trigger_config?.keywords && (
                          <span className="text-xs bg-muted px-2 py-0.5 rounded">
                            {rule.trigger_config.keywords.slice(0, 3).join(', ')}
                            {rule.trigger_config.keywords.length > 3 && ` +${rule.trigger_config.keywords.length - 3} more`}
                          </span>
                        )}
                      </div>

                      {/* Response preview */}
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                        {rule.response_content}
                      </p>

                      {/* Platforms and settings */}
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {/* Platforms */}
                        <div className="flex items-center gap-1">
                          {rule.platforms.map((platform) => {
                            const Icon = PLATFORM_ICONS[platform];
                            const info = PLATFORM_INFO[platform];
                            return (
                              <div
                                key={platform}
                                className="w-5 h-5 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: info.bgColor, color: info.color }}
                                title={info.name}
                              >
                                <Icon className="h-3 w-3" />
                              </div>
                            );
                          })}
                        </div>

                        {/* Delay */}
                        {rule.response_delay_seconds > 0 && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>{rule.response_delay_seconds}s delay</span>
                          </div>
                        )}

                        {/* Daily limit */}
                        <span>Max {rule.max_triggers_per_contact_per_day}/day per contact</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Switch
                        checked={rule.is_active}
                        onCheckedChange={() => handleToggle(rule)}
                      />

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onEditRule(rule)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Rule
                          </DropdownMenuItem>
                          {onDuplicateRule && (
                            <DropdownMenuItem onClick={() => onDuplicateRule(rule)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDeleteClick(rule)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Rule
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Auto-Reply Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{ruleToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AutoReplyRulesList;
